// @ts-nocheck
// app/how-it-works.tsx — Editorial "how it works" page

import React from 'react';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ArrowLeft } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow,
  Headline, Kicker, Numeral, Paper, PageHero, Section, Title, useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const IS_WEB = Platform.OS === 'web';

const PROCESS = [
  { n: '01', kicker: 'התחלה', title: 'אתם כותבים — אנחנו קוראים', body: 'משפט אחד בעברית רגילה. כמה שיותר ספציפי, כמה שיותר אישי. לא צריך להתאמץ עם מילות מפתח — אנחנו מבינים שפה.' },
  { n: '02', kicker: 'הבנה', title: 'ה-AI מפרק את הכוונה', body: 'מאחורי הקלעים: ניתוח רמה, תקציב, פורמט מועדף, יעד, זמן פנוי. הכל מתוך הטקסט שלכם — בלי טופס נפרד.' },
  { n: '03', kicker: 'חיפוש', title: 'סורקים אלפי קורסים', body: 'הקטלוג שלנו כולל מאות מכללות ואלפי קורסים — אונליין ופרונטליים, ארוכים וקצרים, חינמיים ובתשלום.' },
  { n: '04', kicker: 'סינון', title: 'מסירים את הרעש', body: 'קורסים פגי תוקף, ביקורות גרועות, חוסר התאמה לרמה — הכל יורד. נשארות רק האופציות שבאמת רלוונטיות לכם.' },
  { n: '05', kicker: 'התאמה', title: 'שלוש תוצאות. לא יותר.', body: 'שלוש זה המספר הקסום. מספיק כדי להשוות, מעט מספיק כדי להחליט. בלי שמונים עמודי תוצאות.' },
  { n: '06', kicker: 'החלטה', title: 'משווים, נרשמים, מתחילים', body: 'כל התאמה כוללת השוואה ישירה, ביקורות, פירוט מה כלול ומה לא, ומה הצעד הבא. אתם מחליטים.' },
];

const PILLARS = [
  { title: 'הקשר אישי', body: 'אנחנו לוקחים בחשבון רמת ידע, זמן, תקציב, סגנון לימוד מועדף, יעד קריירה.' },
  { title: 'תוכן עדכני', body: 'מאגר שמתעדכן כל יום: קורסים חדשים, ביקורות חדשות, מחירים מעודכנים.' },
  { title: 'הוגנות מערכת', body: 'אף מכללה לא יכולה לקנות לעצמה מקום בראש ההמלצות. נקודה.' },
  { title: 'תיעוד מלא', body: 'כל המלצה כוללת הסבר: למה הקורס הזה הופיע, ומה החולשות שלו.' },
];

export default function HowItWorksPage() {
  const router = useRouter();
  const { isDesktop } = useEditorialBreakpoint();

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>איך זה עובד · Kursi</title>
          <meta name="description" content="כל מה שקורה מאחורי הקלעים כשאתם כותבים שאלה ל-Kursi. שישה צעדים, ארבעה עמודי תווך, אפס ספונסרים." />
          <link rel="canonical" href="https://kursiaii.com/how-it-works" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1100}>
          <PageHero
            kicker="מאחורי הקלעים · גיליון השיטה"
            title={<>שש דקות קריאה{'\n'}על איך <Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>שש שניות</Text>{'\n'}של חיפוש עובדות.</>}
            lede="Kursi נראית פשוטה: כתבת — קיבלת. מאחורי השאלה הזאת נמצא תהליך מורכב של ניתוח כוונה, סריקת קטלוג ודירוג התאמה. הנה איך זה עובד, צעד אחרי צעד."
            meta="זמן קריאה: 6 דקות · עודכן בחודש שעבר"
          />
        </Section>

        <Section py="xxl" px="md" max={1240}>
          <Eyebrow>השיטה — מקרוב</Eyebrow>
          <View style={{ height: 14 }} />
          <Headline level={2}>שישה שלבים. בכל פעם שאתם שואלים.</Headline>
          <View style={{ height: 40 }} />

          <View>
            {PROCESS.map((step, i) => (
              <View key={i} style={s.processRow}>
                <View style={s.processNumCol}>
                  <Numeral size={isDesktop ? 88 : 56}>{step.n}</Numeral>
                </View>
                <View style={s.processBodyCol}>
                  <Kicker>{step.kicker}</Kicker>
                  <View style={{ height: 6 }} />
                  <Title level={3}>{step.title}</Title>
                  <View style={{ height: 10 }} />
                  <Text style={s.body}>{step.body}</Text>
                </View>
              </View>
            ))}
          </View>
        </Section>

        <Section py="md" px="md" max={1240}><DoubleRule /></Section>

        <View style={{ backgroundColor: INK }}>
          <Section py="xxl" px="md" max={1240}>
            <Eyebrow color={ACCENT}>עמודי התווך</Eyebrow>
            <View style={{ height: 14 }} />
            <Text style={s.darkH}>ארבעה דברים שאנחנו לא מתפשרים עליהם.</Text>
            <View style={{ height: 32 }} />
            <View style={s.pillarsGrid}>
              {PILLARS.map((p, i) => (
                <View key={i} style={s.pillar}>
                  <Numeral size={56} color={ACCENT}>{`0${i + 1}`}</Numeral>
                  <View style={{ height: 14 }} />
                  <Text style={s.pillarT}>{p.title}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.pillarB}>{p.body}</Text>
                </View>
              ))}
            </View>
          </Section>
        </View>

        <Section py="xxl" px="md" max={1100}>
          <View style={{ alignItems: 'center' }}>
            <Headline level={2} align="center">מספיק לקרוא. כדאי לנסות.</Headline>
            <View style={{ height: 16 }} />
            <Text style={[s.body, { textAlign: 'center', maxWidth: 560 }]}>
              חמש שניות של כתיבה. שלוש תוצאות. אם זה לא טוב — לא מחויבים לכלום.
            </Text>
            <View style={{ height: 28 }} />
            <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 14 } as any}>
              <EditorialButton label="התחל התאמה" onPress={() => router.push('/chat')} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
              <EditorialButton label="לקטלוג המלא" onPress={() => router.push('/catalog')} variant="secondary" size="lg" />
            </View>
          </View>
        </Section>

        <EditorialFooter
          columns={[
            { title: 'מקטעים', items: [
              { label: 'הקטלוג המלא', href: '/catalog' },
              { label: 'תחומי לימוד', href: '/fields' },
              { label: 'שאלון התאמה', href: '/quiz' },
            ]},
            { title: 'המוצר', items: [
              { label: 'המתודולוגיה', href: '/methodology' },
              { label: 'שאלות נפוצות', href: '/faq' },
              { label: 'אודות', href: '/about' },
            ]},
          ]}
          onNavigate={(href) => router.push(href as any)}
        />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  body: { fontFamily: SANS, fontSize: 17, lineHeight: 28, color: INK_SOFT, textAlign: 'right' },
  processRow: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 28,
    paddingVertical: 28,
    borderTopWidth: 1, borderTopColor: INK,
  } as any,
  processNumCol: { width: 120, alignItems: 'flex-start' },
  processBodyCol: { flex: 1 },
  darkH: { fontFamily: SERIF_BLACK, fontSize: IS_WEB ? 48 : 30, color: PAPER, letterSpacing: -1.1, lineHeight: IS_WEB ? 54 : 36, textAlign: 'right' },
  pillarsGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 0 } as any,
  pillar: {
    width: IS_WEB ? '50%' : '100%',
    padding: 28,
    borderWidth: 1, borderColor: PAPER_SOFT,
    marginLeft: -1, marginTop: -1,
  },
  pillarT: { fontFamily: SERIF_BOLD, fontSize: 26, color: PAPER, letterSpacing: -0.4, textAlign: 'right' },
  pillarB: { fontFamily: SANS, fontSize: 15, color: PAPER_SOFT, lineHeight: 24, textAlign: 'right' },
});
